'use client'

import { useEffect, useRef, useState } from 'react'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
  Button,
  ButtonSizes,
  PopupMenu,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { useBulkSelection } from '@/components/listings/search/BulkSelectionProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { useBulkAddToWatchlistAction } from './useBulkAddToWatchlistAction'

// BulkActionsDropdown only ever mounts on ALP or Storefront (see ListView.tsx) —
// map the analytics screenName each of those pages sets to the surface value
// bulk_watchlist_add_* events expect. Falls back to 'alp' defensively if this
// component is ever mounted somewhere else, so events stay well-formed rather
// than sending an unmapped screenName through as `surface`.
const WATCHLIST_SURFACE_BY_SCREEN_NAME: Partial<
  Record<string, 'alp' | 'storefront'>
> = {
  [ScreenName.AllAuction]: 'alp',
  [ScreenName.SellerStorefront]: 'storefront',
}

export function useClearBulkSelectionOnFilterChange(
  filters: Record<string, unknown>
) {
  const enableBulkActions = useFlag(FeatureFlags.ENABLE_BULK_ACTIONS)
  const { clear } = useBulkSelection()
  const prevKeyRef = useRef<string | null>(null)
  const filtersKey = JSON.stringify(filters)

  useEffect(() => {
    if (!enableBulkActions) return
    if (prevKeyRef.current !== null && prevKeyRef.current !== filtersKey) {
      clear()
    }
    prevKeyRef.current = filtersKey
  }, [enableBulkActions, filtersKey, clear])
}

const DropdownTrigger = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
`

const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media ${Breakpoints.max.small} {
    order: 3;
    width: 100%;
  }
`

const HintText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};

  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.75rem;
  background-color: ${Colors.Semantic.BStock.backgroundSelected};
  border: 1px solid ${Colors.Semantic.Info.highlight};
  border-radius: 0.25rem;
`

const ClearButton = styled(Button)`
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
`

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Colors.Semantic.Info.highlight};
`

const SelectedText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
`

export const BulkActionsDropdown = ({
  onBulkBid,
  selectedListings = [],
}: {
  onBulkBid: () => void
  selectedListings?: CombinedListingSearchResult[]
}) => {
  const enableBulkActions = useFlag(FeatureFlags.ENABLE_BULK_ACTIONS)
  const enableBulkWatchlistAdd = useFlag(FeatureFlags.ENABLE_BULK_WATCHLIST_ADD)
  const { count } = useBulkSelection()
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null)
  const { value: isOpen, setValue: setIsOpen, toggle } = useBoolean(false)
  const { formatMessage } = useIntl()
  const { track, trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const surface = WATCHLIST_SURFACE_BY_SCREEN_NAME[screenName ?? ''] ?? 'alp'
  const { handleClick: handleAddToWatchlist, isPending: isAddingToWatchlist } =
    useBulkAddToWatchlistAction(selectedListings, {
      totalSelectedCount: count,
      track,
      trackButtonClicked,
      formatMessage,
      analytics: {
        screenName,
        surface,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
      },
      messageIds: {
        emptySelection:
          'Listings.ListView.bulkActions.addToWatchlistToasts.emptySelection',
        success: 'Listings.ListView.bulkActions.addToWatchlistToasts.success',
        partialSuccess:
          'Listings.ListView.bulkActions.addToWatchlistToasts.partialSuccess',
        unexpectedError:
          'Listings.ListView.bulkActions.addToWatchlistToasts.unexpectedError',
      },
    })

  useEffect(() => {
    if (count === 0) setIsOpen(false)
  }, [count, setIsOpen])

  if (!enableBulkActions) return null

  const handleToggle = () => {
    if (!isOpen) {
      trackButtonClicked({
        buttonName: 'bulk_actions_button_clicked',
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        selection_count: count,
      })
    }
    toggle()
  }

  const handleBulkBid = () => {
    trackButtonClicked({
      buttonName: 'bulk_bid_initiated',
      screenName,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      selection_count: count,
      listing_id_pairings: selectedListings.map(
        (l) => `${l.storefrontName}: ${l.listingId}`
      ),
    })
    onBulkBid()
  }

  return (
    <DropdownWrapper>
      <DropdownTrigger
        appearance="secondary"
        disabled={count === 0}
        ref={setButtonRef}
        onClick={handleToggle}
      >
        {formatMessage({ id: 'Listings.ListView.bulkActions.button' })}
        <FontAwesomeIcon icon={faAngleDown} />
      </DropdownTrigger>

      {count === 0 && (
        <HintText>
          {formatMessage({ id: 'Listings.ListView.bulkActions.hint' })}
        </HintText>
      )}

      <PopupMenu
        referenceElement={buttonRef}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        align="start"
      >
        <PopupMenu.MenuSection>
          <PopupMenu.ButtonItem onClick={handleBulkBid}>
            {formatMessage({ id: 'Listings.ListView.bulkActions.bulkBid' })}
          </PopupMenu.ButtonItem>
          <PopupMenu.ButtonItem onClick={() => {}} disabled>
            {formatMessage({ id: 'Listings.ListView.bulkActions.compare' })}
          </PopupMenu.ButtonItem>
          <PopupMenu.ButtonItem
            onClick={handleAddToWatchlist}
            disabled={
              !enableBulkWatchlistAdd || isAddingToWatchlist || count === 0
            }
          >
            {formatMessage({
              id: 'Listings.ListView.bulkActions.addToWatchlist',
            })}
          </PopupMenu.ButtonItem>
        </PopupMenu.MenuSection>
      </PopupMenu>
    </DropdownWrapper>
  )
}

export const BulkSelectionBanner = () => {
  const enableBulkActions = useFlag(FeatureFlags.ENABLE_BULK_ACTIONS)
  const { count, clear } = useBulkSelection()
  const { formatMessage } = useIntl()

  if (!enableBulkActions || count === 0) return null

  return (
    <Banner>
      <BannerLeft>
        <FontAwesomeIcon icon={faCircleInfo} />
        <SelectedText>
          {formatMessage(
            { id: 'Listings.ListView.bulkActions.selected' },
            { count }
          )}
        </SelectedText>
      </BannerLeft>
      <ClearButton
        appearance="secondary"
        size={ButtonSizes.small}
        onClick={clear}
      >
        {formatMessage({ id: 'Listings.ListView.bulkActions.clear' })}
      </ClearButton>
    </Banner>
  )
}
